import { useState } from 'react';
import { toast } from 'sonner';

import {
  useSiteCredentialFieldNamesQuery,
  useUpdateSiteCredentialMutation,
} from '@/features/site-credentials/site-credentials.api';
import type { SiteCredential } from '@/features/site-credentials/site-credentials.types';

/** Form state for replacing some of a credential's stored values. A blank field keeps its stored value. */
export const useSiteCredentialEditForm = (
  credential: SiteCredential,
  onSaved: () => void
) => {
  const fieldNames = useSiteCredentialFieldNamesQuery(credential);
  const updateCredential = useUpdateSiteCredentialMutation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string>();

  const changedValues = Object.fromEntries(
    Object.entries(values).filter(([, fieldValue]) => fieldValue !== '')
  );
  const hasChanges = Object.keys(changedValues).length > 0;

  const setFieldValue = (fieldName: string, fieldValue: string) =>
    setValues((current) => ({ ...current, [fieldName]: fieldValue }));

  const save = () => {
    if (!hasChanges || updateCredential.isPending) return;
    setSaveError(undefined);
    updateCredential.mutate(
      { ...credential, values: changedValues },
      {
        onSuccess: () => {
          toast.success('Credential updated');
          onSaved();
        },
        onError: () =>
          setSaveError('Credential could not be updated. Try again.'),
      }
    );
  };

  return {
    fieldNames,
    values,
    setFieldValue,
    hasChanges,
    isSaving: updateCredential.isPending,
    saveError,
    save,
  };
};
